import { createContext } from 'react'

/**
 * Published by whichever ancestor owns the scroll viewport when a page runs in
 * "page scroller" mode (PageContainer `layout="flow"`, or SplitView with
 * `scrollRoot="column"`). Tables read it to learn that they must NOT create a
 * scroller of their own: their sticky thead, sticky first column and sticky
 * pagination all resolve against this element instead.
 *
 * A getter rather than the element itself, because the provider holds a ref —
 * the element is not available on the first render and a state write per mount
 * would churn every table on the page.
 */
export interface TableScrollRoot {
  getElement: () => HTMLElement | null
  /**
   * A zero-height element the owner renders BETWEEN the pinned page header and
   * the scroll viewport. `FlowStickyBar` portals the page's filter bar into it
   * so the bar physically sits outside the scroller: it cannot move on either
   * axis, no matter how far the document is scrolled down or sideways.
   *
   * Real state (not a ref) because consumers must re-render once it mounts —
   * unlike `getElement`, which is only ever read inside effects and handlers.
   * `null` until the owner's first commit, and always `null` on a provider
   * that has no slot to offer.
   */
  toolbarSlot: HTMLElement | null
  /**
   * A full-size, click-through layer the owner renders as a SIBLING of the
   * scroll viewport, covering exactly the scrollport rectangle. Chrome that
   * must stay glued to the viewport rather than to the document — the
   * pagination dock, the column-customize button — is portalled here, so it
   * cannot travel on either axis no matter how far the (much wider)
   * max-content table is scrolled sideways.
   *
   * `position: sticky` cannot do this job: the scrolling document is wider
   * than the scrollport, so a `left: 0` sticky box inside it has no travel
   * left to give and slides away with the rows.
   *
   * State for the same reason as `toolbarSlot`: consumers portal into it, so
   * they must re-render once it mounts. `null` until the owner's first commit,
   * and always `null` on a provider that has no overlay to offer.
   */
  overlaySlot: HTMLElement | null
}

export const TableScrollRootContext = createContext<TableScrollRoot | null>(
  null,
)

/**
 * Fallback for the (rare) case where the context is present but its element
 * has not mounted yet, or a table is rendered inside a flow page through a
 * portal that skipped the provider. Walks up to the first ancestor that
 * actually scrolls vertically.
 */
export function getNearestScrollableAncestor(
  start: HTMLElement | null,
): HTMLElement | null {
  let node = start?.parentElement ?? null
  while (node) {
    const overflowY = getComputedStyle(node).overflowY
    if (overflowY === 'auto' || overflowY === 'scroll') return node
    node = node.parentElement
  }
  return null
}
